import { useSession, useUser } from "@clerk/clerk-react";
import { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  useProjectCacheStore,
  User,
} from "@/store/zustand/projectHistoryStore";
import axios from "axios";
import { getUnexpiredToken } from "@/utils/utils";
import { useSearchParams } from "react-router";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";

export const useBuildProjectLink = () => {
  const { user } = useUser();

  return useCallback(
    (project: any, canvas: any): string => {
      if (!user || !canvas) {
        return "";
      }

      return `/canvases/${canvas.id}/editor`;
    },
    [user],
  );
};

export const useProjectWithUsers = () => {
  const projects = useSelector((state: any) => state.supabaseStore.projects);

  const { session } = useSession();
  const { user } = useUser();

  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const selectedProject = projects[projectId || ""];

  const setUsersForProject = useCanvasStore(
    (state) => state.setUsersForProject,
  );
  const usersForProject = useCanvasStore((state) => state.usersForProject);

  const setProjectUsersCache = useProjectCacheStore(
    (state) => state.setProjectUsers,
  );
  const assignedUsers = useProjectCacheStore((state) =>
    projectId ? state.getProjectUsers(projectId) : undefined,
  );

  useEffect(() => {
    if (!session || !projectId) {
      return;
    }

    if (!usersForProject[projectId] && assignedUsers) {
      setUsersForProject(projectId, assignedUsers);
    }

    async function fetchUsers(projectId: string) {
      if (!session) {
        return;
      }

      const supabaseJWT = await getUnexpiredToken(session);
      if (!supabaseJWT) {
        console.error("Failed to get Supabase JWT, cannot fetch users.");
        return;
      }

      const axiosInstance = axios.create({
        baseURL: import.meta.env.VITE_BACKEND_BASE_URL,
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
        },
      });

      try {
        const usersResp = await axiosInstance.get(
          `/projects/${projectId}/users`,
        );
        const apiUsers: User[] =
          usersResp?.data?.users?.map(
            (user: any): User => ({
              name: user.name,
              email: user.email,
              id: user.auth0_user_id,
              picture: user.picture,
              is_selected: false,
            }),
          ) || [];

        setUsersForProject(projectId, apiUsers);
        setProjectUsersCache(projectId, apiUsers);
      } catch (e) {
        console.error(`Error fetching users for project ${projectId}:`, e);
      }
    }

    if (projectId) {
      fetchUsers(projectId);
    }
  }, [projectId, Boolean(session)]);

  return useMemo(() => {
    if (!user || !selectedProject || !session || !projectId) {
      return null;
    }

    if (usersForProject[projectId] && usersForProject[projectId].length > 0) {
      return {
        ...selectedProject,
        users: usersForProject[projectId],
      };
    }

    return selectedProject;
  }, [selectedProject, projectId, session, user, usersForProject]);
};
